import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, Calendar, Activity, Trash2, AlertTriangle, CheckCircle, AlertCircle, XCircle, Loader2, Printer, Sparkles } from 'lucide-react';
import ReportMarkdown from '../components/ReportMarkdown';
import { reportsService, SavedReport } from '../lib/reportsService';

const riskColors: Record<string, string> = { low: 'bg-green-100 text-green-900 border-green-400', moderate: 'bg-yellow-100 text-yellow-900 border-yellow-400', high: 'bg-orange-100 text-orange-900 border-orange-400', critical: 'bg-red-100 text-red-900 border-red-400' };
const riskIcons: Record<string, typeof CheckCircle> = { low: CheckCircle, moderate: AlertCircle, high: AlertTriangle, critical: XCircle };

export default function ReportDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [report, setReport] = useState<SavedReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => { if (id) loadReport(id); }, [id]);

  const loadReport = async (reportId: string) => {
    setLoading(true);
    const { data, error: loadError } = await reportsService.getReport(reportId);
    if (loadError) setError(loadError.message);
    else if (!data) setError('Report not found');
    else setReport(data);
    setLoading(false);
  };

  const handleDelete = async () => {
    if (!report || !confirm('Delete this report?')) return;
    setDeleting(true);
    const { success } = await reportsService.deleteReport(report.id);
    if (success) navigate('/reports', { replace: true });
    else setDeleting(false);
  };

  const formatDate = (d: string) => new Date(d).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' });

  if (loading) return <div className="min-h-screen bg-gradient-to-br from-blue-50 via-slate-50 to-blue-100 flex items-center justify-center"><Loader2 className="w-12 h-12 text-blue-600 animate-spin" /></div>;

  if (error || !report) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-slate-50 to-blue-100">
        <header className="bg-white/80 backdrop-blur-sm border-b border-blue-100 sticky top-0 z-10"><div className="max-w-4xl mx-auto px-4 py-4"><Link to="/reports" className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900"><ArrowLeft className="w-5 h-5" />Back</Link></div></header>
        <main className="max-w-4xl mx-auto px-4 py-16"><div className="bg-white rounded-xl shadow-lg p-8 text-center"><AlertCircle className="w-16 h-16 text-red-600 mx-auto mb-4" /><h2 className="text-2xl font-bold mb-2">Report Not Found</h2><Link to="/reports" className="text-blue-600 hover:underline">Back to Reports</Link></div></main>
      </div>
    );
  }

  const RiskIcon = riskIcons[report.risk_level.toLowerCase()] || AlertCircle;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-slate-50 to-blue-100">
      <header className="bg-white/80 backdrop-blur-sm border-b border-blue-100 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/reports" className="text-gray-600 hover:text-gray-900"><ArrowLeft className="w-5 h-5" /></Link>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-600 rounded-lg shadow-md"><FileText className="w-5 h-5 text-white" /></div>
                <div><h1 className="text-xl font-bold text-gray-900">{report.calculator_short_name}</h1><p className="text-sm text-gray-600">Saved Report</p></div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => window.print()} className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg"><Printer className="w-5 h-5" /></button>
              <button onClick={handleDelete} disabled={deleting} className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50">{deleting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Trash2 className="w-5 h-5" />}</button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-900">{report.calculator_name}</h2>
          <div className="flex items-center gap-2 mt-2 text-sm text-gray-500"><Calendar className="w-4 h-4" />{formatDate(report.created_at)}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
            <div className="text-center p-6 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-xl"><p className="text-sm text-blue-100 mb-2">Score</p><p className="text-5xl font-bold text-white">{report.score}</p></div>
            <div className={`p-4 rounded-xl border-2 flex flex-col justify-center ${riskColors[report.risk_level.toLowerCase()] || 'bg-gray-100'}`}>
              <div className="flex items-center gap-2 mb-2"><RiskIcon className="w-6 h-6" /><span className="font-bold text-lg capitalize">{report.risk_level} Risk</span></div>
              <p className="font-medium">{report.interpretation}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2"><Activity className="w-5 h-5 text-blue-600" />Patient Parameters</h3>
          <div className="bg-gray-50 rounded-lg p-4 grid grid-cols-2 sm:grid-cols-3 gap-4">
            {Object.entries(report.input_values).map(([key, value]) => (
              <div key={key}><p className="text-xs text-gray-500 uppercase">{key.replace(/([A-Z])/g, ' $1').trim()}</p><p className="font-semibold text-gray-900">{String(value)}</p></div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center gap-2 text-purple-700 mb-4"><Sparkles className="w-5 h-5" /><h3 className="text-lg font-bold">AI Clinical Interpretation</h3></div>
          <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-6 border border-purple-200">
            <div className="report-surface rounded-xl border border-slate-100 bg-white p-5 shadow-sm sm:p-6">
              <ReportMarkdown content={report.ai_interpretation ?? ''} />
            </div>
          </div>
          <p className="text-xs text-gray-500 italic mt-4"><strong>Disclaimer:</strong> AI interpretation for decision support only. Always use clinical judgment.</p>
        </div>

        <div className="flex gap-4 mt-8">
          <Link to="/reports" className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium"><ArrowLeft className="w-5 h-5" />All Reports</Link>
          <Link to={`/calculators/${report.calculator_id}`} className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium">Calculate Again</Link>
        </div>
      </main>
    </div>
  );
}
